import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tacticalApi, fiveStarApi } from '../lib/api';
import { ClipboardList, Star, ArrowRight, TrendingUp, Calendar, Target, Trophy } from 'lucide-react';

const monthName = (m) => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1];

const ServiceCard = ({ to, accent, icon: Icon, title, desc, stats, testId }) => {
  const colors = {
    cyan: {
      bar: 'bg-gradient-to-r from-cyan-400 to-cyan-600',
      icon: 'bg-cyan-50 text-cyan-600',
      arrow: 'bg-cyan-50 text-cyan-600',
      ring: 'hover:shadow-[0_12px_36px_rgba(0,184,212,0.18)] hover:border-cyan-300',
    },
    magenta: {
      bar: 'bg-gradient-to-r from-fuchsia-400 to-fuchsia-600',
      icon: 'bg-fuchsia-50 text-fuchsia-600',
      arrow: 'bg-fuchsia-50 text-fuchsia-600',
      ring: 'hover:shadow-[0_12px_36px_rgba(213,0,249,0.18)] hover:border-fuchsia-300',
    },
  }[accent];

  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group relative block bg-white border border-slate-200 rounded-2xl p-8 shadow-sm transition-all hover:-translate-y-1 ${colors.ring} overflow-hidden`}
    >
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${colors.bar}`} />
      <div className={`absolute right-5 top-7 w-9 h-9 rounded-full flex items-center justify-center transition-transform group-hover:translate-x-1 ${colors.arrow}`}>
        <ArrowRight className="w-4 h-4" />
      </div>

      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${colors.icon}`}>
        <Icon className="w-7 h-7" />
      </div>
      <div className="text-[22px] font-bold text-slate-900 mb-2 tracking-tight">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed mb-5 max-w-[460px]">{desc}</div>

      <div className="flex items-center gap-7 pt-5 border-t border-slate-100">
        {stats}
      </div>
    </Link>
  );
};

const Stat = ({ value, label }) => (
  <div className="flex flex-col gap-0.5">
    <div className="text-xl font-extrabold text-slate-900 tracking-tight">{value}</div>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{label}</div>
  </div>
);

const OverviewCard = ({ icon: Icon, label, value, sub, accent }) => {
  const colorClass = {
    cyan: 'text-cyan-600',
    magenta: 'text-fuchsia-600',
    lime: 'text-emerald-600',
    gold: 'text-amber-600',
  }[accent] || 'text-slate-700';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:-translate-y-0.5 transition">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-2.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-[28px] font-extrabold tracking-tight ${colorClass}`}>{value}</div>
      <div className="text-[11px] text-slate-400 mt-1 font-medium">{sub}</div>
    </div>
  );
};

const GrowthHubHome = () => {
  const { user } = useAuth();
  const [tactical, setTactical] = useState(null);
  const [myReviews, setMyReviews] = useState([]);
  const [managedReviews, setManagedReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [t, mine, managed] = await Promise.all([
          tacticalApi.getCurrent().catch(() => null),
          fiveStarApi.listMyReviews(true).catch(() => []),
          fiveStarApi.listManagedReviews(true).catch(() => []),
        ]);
        if (cancel) return;
        setTactical(t);
        setMyReviews(mine || []);
        setManagedReviews(managed || []);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // Intentionally run once on mount; api refs are stable module-level imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const monthLabel = `${['January','February','March','April','May','June','July','August','September','October','November','December'][now.getMonth()]} ${now.getFullYear()}`;

  const reviewsPending = managedReviews.filter(r => !r.is_archived && r.status !== 'discussed').length;
  const reviewsCompleted = managedReviews.filter(r => r.is_archived || r.status === 'discussed').length;
  const myPendingReview = myReviews.find(r => !r.is_archived && r.status === 'discussion_pending');
  const tacticalLabel = tactical ? `${monthName(tactical.month)} '${String(tactical.year).slice(-2)}` : '—';
  const facilitiesCount = tactical?.facility_sites?.length || 0;

  return (
    <div className="px-12 pt-10 pb-20 max-w-[1400px]" data-testid="growth-hub-home">
      <div className="mb-9">
        <h1 className="text-[34px] font-extrabold tracking-tight text-slate-900 mb-1.5">
          Welcome back, {user?.given_name || (user?.display_name || '').split(' ')[0] || 'there'}
        </h1>
        <p className="text-[15px] text-slate-500">
          {monthLabel} —{' '}
          <span className="font-semibold bg-gradient-to-r from-cyan-500 to-fuchsia-500 bg-clip-text text-transparent">
            Elevate People. Accelerate Potential.
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <ServiceCard
          to="/tacticals"
          accent="cyan"
          icon={ClipboardList}
          title="Tacticals"
          desc="Monthly performance tracking — Development goals, Scorecard, Lightning Round, and Facility Overview."
          testId="card-tacticals"
          stats={
            <>
              <Stat value={tacticalLabel} label="Current" />
              <Stat value={facilitiesCount} label="Facility Sites" />
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase bg-cyan-50 text-cyan-700">
                {tactical ? 'Active' : 'None Yet'}
              </span>
            </>
          }
        />
        <ServiceCard
          to="/five-star"
          accent="magenta"
          icon={Star}
          title="5 Star Feedback & Development"
          desc="Multi-stage performance reviews — Manager & Area Manager evaluations, synthesis, sign-off, and employee discussion."
          testId="card-five-star"
          stats={
            <>
              <Stat value={reviewsPending} label="In Progress" />
              <Stat value={reviewsCompleted} label="Completed" />
              {myPendingReview && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase bg-fuchsia-50 text-fuchsia-700">
                  Review Ready
                </span>
              )}
            </>
          }
        />
      </div>

      <div className="text-[13px] font-bold uppercase tracking-[1px] text-slate-400 mb-4">Quick Overview</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-9">
        <OverviewCard icon={Calendar} label="Current Tactical" value={tacticalLabel} sub={`${facilitiesCount} facility sites`} accent="cyan" />
        <OverviewCard icon={Star} label="Open Reviews" value={reviewsPending} sub="Across your team" accent="magenta" />
        <OverviewCard icon={TrendingUp} label="My Reviews" value={myReviews.length} sub="Total in history" accent="lime" />
        <OverviewCard icon={Trophy} label="Completed" value={reviewsCompleted} sub="Archived reviews" accent="gold" />
      </div>

      {!loading && managedReviews.length === 0 && myReviews.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl px-8 py-10 text-center">
          <Target className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <div className="text-sm text-slate-600 mb-1">No reviews yet.</div>
          <div className="text-xs text-slate-400 mb-4">Head into 5 Star Feedback to start your first one.</div>
          <Link
            to="/five-star"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white text-sm font-semibold shadow hover:shadow-lg transition"
            data-testid="empty-go-to-five-star"
          >
            Open 5 Star Feedback <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default GrowthHubHome;
